import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface EmpresaFinanceira {
  id: string;
  slug: string;
  razao_social: string;
  nome_fantasia: string;
  cnpj: string;
  contabilidade_email: string | null;
  ativa: boolean;
}

export interface ContaBancariaEmpresa {
  id: string;
  empresa_id: string;
  banco: 'inter' | 'c6';
  apelido: string;
  modo: 'api' | 'manual';
  ativa: boolean;
}

interface EmpresaFinanceiraContextValue {
  carregando: boolean;
  empresas: EmpresaFinanceira[];
  selecao: string;
  setSelecao: (valor: string) => void;
  empresaId: string | null;
  empresa: EmpresaFinanceira | null;
  contaBancaria: ContaBancariaEmpresa | null;
  exigirEmpresa: () => string | null;
}

const STORAGE_KEY = 'financeiro.empresaSelecao';
const EmpresaFinanceiraContext = createContext<EmpresaFinanceiraContextValue | null>(null);

export function formatarCnpj(cnpj: string) {
  const digitos = String(cnpj || '').replace(/\D/g, '');
  if (digitos.length !== 14) return cnpj || '';
  return digitos.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

export function EmpresaFinanceiraProvider({ children }: { children: ReactNode }) {
  const [empresas, setEmpresas] = useState<EmpresaFinanceira[]>([]);
  const [contasBancarias, setContasBancarias] = useState<ContaBancariaEmpresa[]>([]);
  const [selecao, setSelecaoState] = useState('');
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let ativo = true;
    (async () => {
      const [{ data: emps, error: erroEmpresas }, { data: contas, error: erroContas }] = await Promise.all([
        (supabase as any).from('empresas').select('*').eq('ativa', true).order('nome_fantasia'),
        (supabase as any).from('contas_bancarias').select('*').eq('ativa', true),
      ]);
      if (!ativo) return;
      if (erroEmpresas) console.error(erroEmpresas);
      if (erroContas) console.error(erroContas);

      const lista = (emps || []) as EmpresaFinanceira[];
      setEmpresas(lista);
      setContasBancarias((contas || []) as ContaBancariaEmpresa[]);
      const salva = localStorage.getItem(STORAGE_KEY) || '';
      if (salva === 'consolidado' || lista.some((empresa) => empresa.id === salva)) {
        setSelecaoState(salva);
      } else {
        const rst = lista.find((empresa) => empresa.slug === 'rst-servicos');
        setSelecaoState(rst?.id || lista[0]?.id || 'consolidado');
      }
      setCarregando(false);
    })();
    return () => {
      ativo = false;
    };
  }, []);

  const setSelecao = useCallback((valor: string) => {
    setSelecaoState(valor);
    localStorage.setItem(STORAGE_KEY, valor);
  }, []);

  const empresa = empresas.find((item) => item.id === selecao) || null;
  const empresaId = empresa?.id ?? null;
  const contaBancaria = contasBancarias.find((conta) => conta.empresa_id === empresaId && conta.ativa) || null;

  const exigirEmpresa = useCallback(() => {
    if (!empresaId) {
      toast.error('Selecione RST Serviços ou Sun7 Energy para lançar. O modo consolidado só permite consulta.');
      return null;
    }
    return empresaId;
  }, [empresaId]);

  const value = useMemo(
    () => ({
      carregando,
      empresas,
      selecao,
      setSelecao,
      empresaId,
      empresa,
      contaBancaria,
      exigirEmpresa,
    }),
    [carregando, empresas, selecao, setSelecao, empresaId, empresa, contaBancaria, exigirEmpresa]
  );

  return <EmpresaFinanceiraContext.Provider value={value}>{children}</EmpresaFinanceiraContext.Provider>;
}

export function useEmpresaFinanceira() {
  const contexto = useContext(EmpresaFinanceiraContext);
  if (!contexto) {
    throw new Error('useEmpresaFinanceira deve ser usado na página Financeiro');
  }
  return contexto;
}
